import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import ScienceIcon from "@mui/icons-material/Science";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import EuroIcon from "@mui/icons-material/Euro";
import GavelIcon from "@mui/icons-material/Gavel";
import FeedIcon from "@mui/icons-material/Feed";
import VerifiedIcon from "@mui/icons-material/Verified";
import ContactsIcon from "@mui/icons-material/Contacts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import MedicationIcon from "@mui/icons-material/Medication";
import { api, triggerSync } from "../services/api";

type SyncKey = "clinical-trials" | "pricing" | "fda" | "who-trials" | "ema" | "patents" | "news" | "fda-approvals" | "approval-crossref" | "device-enrichment" | "dosing-enrichment" | "regulatory-milestones" | "trial-history" | "validate-pipeline" | "contacts" | "google-contacts" | "press-releases" | "company-enrichment" | "rebuild-programs" | "ml-scoring" | "pubmed" | "press-release-backfill" | "test-alignment" | "trial-field-backfill";

interface SyncJob {
  key: SyncKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  schedule: string;
  category: "core" | "global" | "enrichment" | "contacts";
}

const syncJobs: SyncJob[] = [
  // Core data sources
  {
    key: "clinical-trials",
    label: "ClinicalTrials.gov",
    description:
      "Phase 1-3 injectable drug trials (active/recruiting only). Discovers new candidates, detects phase changes, and calculates probability of approval.",
    icon: <ScienceIcon sx={{ fontSize: 36, color: "primary.main" }} />,
    schedule: "Daily at 2:00 AM",
    category: "core",
  },
  {
    key: "fda",
    label: "FDA Approvals + Biologics",
    description:
      "Injectable drug approvals from openFDA (6,600+ records) and BLA biologics. Enriches pipeline candidates with generic names and manufacturer data.",
    icon: <LocalHospitalIcon sx={{ fontSize: 36, color: "success.main" }} />,
    schedule: "Daily at 4:00 AM",
    category: "core",
  },
  {
    key: "pricing",
    label: "CMS Pricing (ASP + NADAC)",
    description:
      "Medicare Part B drug spending (ASP-derived pricing) and NADAC acquisition costs for injectable drugs. Powers WAC projections.",
    icon: <AttachMoneyIcon sx={{ fontSize: 36, color: "secondary.main" }} />,
    schedule: "Weekly on Mondays at 3:00 AM",
    category: "core",
  },
  // Global sources
  {
    key: "who-trials",
    label: "WHO Global Trials (ICTRP)",
    description:
      "International clinical trials from EU, China, Japan, India, and 15+ other registries. Provides global pipeline coverage beyond US trials.",
    icon: <PublicIcon sx={{ fontSize: 36, color: "#1565c0" }} />,
    schedule: "Weekly on Tuesdays at 2:00 AM",
    category: "global",
  },
  {
    key: "ema",
    label: "EMA (European Medicines Agency)",
    description:
      "EU-authorised injectable medicines, CHMP opinions (pre-approval signals), and marketing authorisation data from Europe.",
    icon: <EuroIcon sx={{ fontSize: 36, color: "#6a1b9a" }} />,
    schedule: "Weekly on Wednesdays at 2:00 AM",
    category: "global",
  },
  // Enrichment sources
  {
    key: "company-enrichment",
    label: "Company Size & Tier Enrichment",
    description:
      "Enriches organizations with company size data (employee count, revenue, founded year) from People Data Labs and Wikidata. Computes tier classification: Big Pharma, Mid-Cap, Emerging, Startup.",
    icon: <BusinessIcon sx={{ fontSize: 36, color: "#1565c0" }} />,
    schedule: "Weekly",
    category: "enrichment",
  },
  {
    key: "patents",
    label: "Orange Book (Patents & Exclusivity)",
    description:
      "Patent expiry dates and exclusivity periods for injectable drugs from the FDA Orange Book. Helps estimate generic entry timing.",
    icon: <GavelIcon sx={{ fontSize: 36, color: "#c62828" }} />,
    schedule: "Monthly on 1st at 3:00 AM",
    category: "enrichment",
  },
  {
    key: "fda-approvals",
    label: "FDA CDER Novel Approvals",
    description:
      "Novel drug therapy approvals from FDA CDER and recent 6-month approval reports for injectable products.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#2e7d32" }} />,
    schedule: "Weekly on Fridays at 2:00 AM",
    category: "enrichment",
  },
  {
    key: "device-enrichment",
    label: "Device & Route Enrichment",
    description:
      "Enriches pipeline candidates with specific route (IV, SubQ, IM), delivery device (PFS, autoinjector, pen, vial, YpsoMate, BD UltraSafe, etc.), and dosage form (solution, lyophilized powder) from FDA drug labels.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#00838f" }} />,
    schedule: "After FDA sync",
    category: "enrichment",
  },
  {
    key: "dosing-enrichment",
    label: "Dosing & Administration Enrichment",
    description:
      "Parses dosing frequency, dose amount, injection volume, self-administration status, and administration setting from ClinicalTrials.gov intervention descriptions. Handles abbreviations like QW, Q2W, Q4W, BIW, SC, IV, IM.",
    icon: <MedicationIcon sx={{ fontSize: 36, color: "#1565c0" }} />,
    schedule: "After Device sync",
    category: "enrichment",
  },
  {
    key: "regulatory-milestones",
    label: "Regulatory Milestones",
    description:
      "Enriches pipeline drugs with FDA designations (Breakthrough, Fast Track, Orphan, Priority Review) from openFDA and ClinicalTrials.gov. No Claude credits used.",
    icon: <GavelIcon sx={{ fontSize: 36, color: "#c62828" }} />,
    schedule: "Weekly",
    category: "enrichment",
  },
  {
    key: "trial-history",
    label: "Trial History Backfill",
    description:
      "Fetches complete clinical trial history (including completed Phase 1/2 trials) for pipeline candidates that only have 1 trial record. Gives a full development timeline.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#4527a0" }} />,
    schedule: "Weekly",
    category: "enrichment",
  },
  {
    key: "validate-pipeline",
    label: "Pipeline Validation (AI)",
    description:
      "Uses Claude AI to batch-review pipeline entries and flag problems: already-approved drugs used as comparators, drug class names instead of actual drugs, oral drugs misclassified as injectable, and junk entries. Run after ClinicalTrials.gov sync.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#c62828" }} />,
    schedule: "After each CT.gov sync",
    category: "enrichment",
  },
  {
    key: "approval-crossref",
    label: "Approval Cross-Reference",
    description:
      "Cross-references all pipeline candidates against FDA approved drugs. Flags already-approved drugs, maps brand names (e.g., rilonacept → ARCALYST), and hides them from the pipeline view.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#1a237e" }} />,
    schedule: "After each FDA sync",
    category: "enrichment",
  },
  {
    key: "news",
    label: "Pharma News Feeds",
    description:
      "Pipeline and BD intelligence from GlobeNewswire, PR Newswire, BioSpace, FiercePharma, and FDA press releases. Flags manufacturing partnerships and trial results.",
    icon: <FeedIcon sx={{ fontSize: 36, color: "#00838f" }} />,
    schedule: "Every 6 hours",
    category: "enrichment",
  },
  {
    key: "press-releases",
    label: "Press Release Matching",
    description:
      "Fetches press releases from 20 major pharma company feeds (Pfizer, Roche, Novartis, Merck, etc.) plus news feeds. Matches articles to pipeline drugs, classifies events (trial results, regulatory filings, FDA actions, partnerships), and estimates timeline impact.",
    icon: <FeedIcon sx={{ fontSize: 36, color: "#4527a0" }} />,
    schedule: "Daily at 5:00 AM",
    category: "enrichment",
  },
  // Contact Discovery
  {
    key: "google-contacts",
    label: "Research & Publication Contacts",
    description:
      "Discovers contacts from academic APIs: ORCID researcher profiles at pharma companies, CrossRef publication authors with pharma affiliations, and PubMed authors of injectable formulation/CMC publications. All via official APIs.",
    icon: <ContactsIcon sx={{ fontSize: 36, color: "#1565c0" }} />,
    schedule: "Weekly on Saturdays at 1:00 AM",
    category: "contacts",
  },
  {
    key: "contacts",
    label: "Contact Discovery Engine",
    description:
      "Full contact discovery pipeline: extracts BD-relevant contacts from press release quotes, ClinicalTrials.gov investigators, conference speakers, patent inventors, and job posting signals. Discovers and verifies email addresses.",
    icon: <ContactsIcon sx={{ fontSize: 36, color: "#ad1457" }} />,
    schedule: "Weekly on Saturdays at 2:00 AM",
    category: "contacts",
  },
  {
    key: "pubmed",
    label: "PubMed Publications",
    description:
      "Fetches clinical trial publication counts from PubMed for each pipeline drug. Higher publication count indicates stronger evidence base and research interest. Feeds into ML probability model.",
    icon: <FeedIcon sx={{ fontSize: 36, color: "#2e7d32" }} />,
    schedule: "After trial history",
    category: "enrichment",
  },
  {
    key: "test-alignment",
    label: "Test Probability Alignment",
    description:
      "Diagnostic: scores top 5 drugs three ways (per-trial model, stored DB value, timeline path) and reports misalignment. Check Railway logs for results.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#d84315" }} />,
    schedule: "Run after ML Scoring",
    category: "enrichment",
  },
  {
    key: "trial-field-backfill",
    label: "Trial Field Backfill",
    description:
      "Re-fetches CT.gov for trials whose ML feature columns are stale (NULL or default-False from migrations). Closes the gap between training data and production trial fields. Idempotent — safe to re-run.",
    icon: <VerifiedIcon sx={{ fontSize: 36, color: "#1565c0" }} />,
    schedule: "After CT.gov sync",
    category: "enrichment",
  },
  {
    key: "press-release-backfill",
    label: "Press Release Backfill",
    description:
      "Searches for historical press releases for pipeline drugs with limited event data. Processes 100 candidates per batch — run multiple times to cover the full pipeline. Finds FDA actions, designations, partnerships, and trial results.",
    icon: <FeedIcon sx={{ fontSize: 36, color: "#6a1b9a" }} />,
    schedule: "Run multiple batches",
    category: "enrichment",
  },
  {
    key: "ml-scoring",
    label: "ML Probability Scoring",
    description:
      "Scores all pipeline drugs using the Markov transition model. Computes approval probability and estimated launch year from trial data, designations, endpoints, articles, and prior phase history. Run LAST after all data collection.",
    icon: <BusinessIcon sx={{ fontSize: 36, color: "#e65100" }} />,
    schedule: "After all data collection",
    category: "enrichment",
  },
];

interface SyncResult {
  key: string;
  status: "success" | "error";
  data?: Record<string, unknown>;
  error?: string;
}

const categoryLabels: Record<string, string> = {
  core: "Core Pipeline Data",
  global: "Global Coverage",
  enrichment: "Enrichment & Intelligence",
  contacts: "Contact Discovery",
};

// Persist running tasks in localStorage so they survive navigation
function getRunningTasks(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem("sync_tasks") || "{}");
  } catch { return {}; }
}
function setRunningTask(key: string, taskId: string) {
  const tasks = getRunningTasks();
  tasks[key] = taskId;
  localStorage.setItem("sync_tasks", JSON.stringify(tasks));
}
function clearRunningTask(key: string) {
  const tasks = getRunningTasks();
  delete tasks[key];
  localStorage.setItem("sync_tasks", JSON.stringify(tasks));
}

export default function DataSync() {
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<SyncResult[]>([]);
  const [runningAll, setRunningAll] = useState(false);

  // On mount, check for any tasks that were running when we left the page
  React.useEffect(() => {
    const savedTasks = getRunningTasks();
    Object.entries(savedTasks).forEach(([key, taskId]) => {
      setRunning((prev) => ({ ...prev, [key]: true }));
      pollForResult(taskId, key);
    });
  }, []);

  const pollForResult = (taskId: string, jobKey: string) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/sync/status/${taskId}`);
        if (data.status === "complete") {
          clearInterval(interval);
          clearRunningTask(jobKey);
          setResults((prev) => [
            { key: jobKey, status: "success", data: data.result },
            ...prev.filter((r) => r.key !== jobKey),
          ]);
          setRunning((prev) => ({ ...prev, [jobKey]: false }));
        } else if (data.status === "error") {
          clearInterval(interval);
          clearRunningTask(jobKey);
          setResults((prev) => [
            { key: jobKey, status: "error", error: data.error },
            ...prev.filter((r) => r.key !== jobKey),
          ]);
          setRunning((prev) => ({ ...prev, [jobKey]: false }));
        }
      } catch (err: any) {
        // If 404, the task is gone (server restarted) — stop polling
        if (err?.response?.status === 404) {
          clearInterval(interval);
          clearRunningTask(jobKey);
          setResults((prev) => [
            { key: jobKey, status: "error", error: "Task lost — server restarted. Please re-run." },
            ...prev.filter((r) => r.key !== jobKey),
          ]);
          setRunning((prev) => ({ ...prev, [jobKey]: false }));
        }
        // Other errors: keep polling (transient network issue)
      }
    }, 3000);
  };

  const handleClearStuck = () => {
    localStorage.removeItem("sync_tasks");
    setRunning({});
    setResults([]);
  };

  const handleSync = async (job: SyncJob) => {
    setRunning((prev) => ({ ...prev, [job.key]: true }));
    setResults((prev) => prev.filter((r) => r.key !== job.key));

    try {
      const data = await triggerSync(job.key);
      const taskId = (data as any).task_id;
      if (taskId) {
        // Save task ID and start polling — survives page navigation
        setRunningTask(job.key, taskId);
        pollForResult(taskId, job.key);
      } else {
        // Immediate response (legacy)
        setResults((prev) => [
          { key: job.key, status: "success", data },
          ...prev.filter((r) => r.key !== job.key),
        ]);
        setRunning((prev) => ({ ...prev, [job.key]: false }));
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Sync failed. Is the backend running?";
      setResults((prev) => [
        { key: job.key, status: "error", error: message },
        ...prev.filter((r) => r.key !== job.key),
      ]);
      setRunning((prev) => ({ ...prev, [job.key]: false }));
    }
  };

  const handleSyncAll = async () => {
    setRunningAll(true);
    // Run sequentially — wait for each to complete before starting next
    for (const job of syncJobs) {
      await new Promise<void>((resolve) => {
        const originalHandleSync = async () => {
          setRunning((prev) => ({ ...prev, [job.key]: true }));
          setResults((prev) => prev.filter((r) => r.key !== job.key));
          try {
            const data = await triggerSync(job.key);
            const taskId = (data as any).task_id;
            if (taskId) {
              // Poll until complete
              const waitForCompletion = async (): Promise<void> => {
                try {
                  const { data: status } = await api.get(`/sync/status/${taskId}`);
                  if (status.status === "complete") {
                    setResults((prev) => [
                      { key: job.key, status: "success", data: status.result },
                      ...prev.filter((r) => r.key !== job.key),
                    ]);
                    setRunning((prev) => ({ ...prev, [job.key]: false }));
                    resolve();
                  } else if (status.status === "error") {
                    setResults((prev) => [
                      { key: job.key, status: "error", error: status.error },
                      ...prev.filter((r) => r.key !== job.key),
                    ]);
                    setRunning((prev) => ({ ...prev, [job.key]: false }));
                    resolve();
                  } else {
                    setTimeout(waitForCompletion, 3000);
                  }
                } catch {
                  setTimeout(waitForCompletion, 5000);
                }
              };
              await waitForCompletion();
            } else {
              setResults((prev) => [
                { key: job.key, status: "success", data },
                ...prev.filter((r) => r.key !== job.key),
              ]);
              setRunning((prev) => ({ ...prev, [job.key]: false }));
              resolve();
            }
          } catch (err: any) {
            setResults((prev) => [
              { key: job.key, status: "error", error: err?.message || "Failed" },
              ...prev.filter((r) => r.key !== job.key),
            ]);
            setRunning((prev) => ({ ...prev, [job.key]: false }));
            resolve();
          }
        };
        originalHandleSync();
      });
    }
    setRunningAll(false);
  };

  const getResult = (key: string) => results.find((r) => r.key === key);

  const categories = ["core", "global", "enrichment", "contacts"];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Data Sync</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {Object.values(running).some(Boolean) && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleClearStuck}
              size="large"
            >
              Clear Stuck Tasks
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={
              runningAll ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />
            }
            onClick={handleSyncAll}
            disabled={runningAll}
            size="large"
          >
            {runningAll ? "Syncing All..." : "Sync All Sources"}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        10 data sources across clinical trials, regulatory agencies, pricing databases,
        patent records, pharma news, and contact discovery. Syncs run automatically on
        schedule or click to sync manually.
      </Alert>

      {categories.map((category) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
            {categoryLabels[category]}
          </Typography>
          <Stack spacing={2}>
            {syncJobs
              .filter((job) => job.category === category)
              .map((job) => {
                const result = getResult(job.key);
                const isRunning = running[job.key];

                return (
                  <Card key={job.key}>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ mt: 0.5 }}>{job.icon}</Box>
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6">{job.label}</Typography>
                            <Button
                              variant="outlined"
                              startIcon={
                                isRunning ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SyncIcon />
                                )
                              }
                              onClick={() => handleSync(job)}
                              disabled={isRunning}
                            >
                              {isRunning ? "Syncing..." : "Sync Now"}
                            </Button>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {job.description}
                          </Typography>
                          <Chip
                            label={job.schedule}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 12 }}
                          />

                          {result && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              {result.status === "success" ? (
                                <Alert
                                  severity="success"
                                  icon={<CheckCircleIcon />}
                                  sx={{ mt: 1 }}
                                >
                                  <Typography variant="body2" fontWeight={600}>
                                    Sync completed successfully
                                  </Typography>
                                  {result.data && (
                                    <Box sx={{ mt: 1 }}>
                                      {Object.entries(result.data)
                                        .filter(([k]) => k !== "status")
                                        .map(([key, val]) => (
                                          <Typography key={key} variant="body2">
                                            {key.replace(/_/g, " ")}: <strong>{typeof val === "object" ? JSON.stringify(val) : String(val)}</strong>
                                          </Typography>
                                        ))}
                                    </Box>
                                  )}
                                </Alert>
                              ) : (
                                <Alert
                                  severity="error"
                                  icon={<ErrorIcon />}
                                  sx={{ mt: 1 }}
                                >
                                  <Typography variant="body2" fontWeight={600}>
                                    Sync failed
                                  </Typography>
                                  <Typography variant="body2">
                                    {result.error}
                                  </Typography>
                                </Alert>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
